// client/src/services/taxRag.ts
// API client for the Tax Research RAG admin tab.

export type TaxRagLatestRun = {
  runId: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'partial';
  mode: RunMode;
  startedAt: string | null;
  finishedAt: string | null;
  itemsTotal: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsSkipped: number;
  itemsFailed: number;
  chunksUpserted: number;
  estimatedCostUsd: number;
};

export type TaxRagSource = {
  key: string;
  label: string;
  description: string;
  url: string;
  kind: 'initial' | 'daily' | 'corrections' | 'other';
  filename: string;
  size: number;
  lastModified: string | null;
  processed: boolean;
  latestRun: TaxRagLatestRun | null;
};

export type TaxRagChunkConfig = {
  targetTokens?: number;
  maxTokens?: number;
  overlapTokens?: number;
  minChunkTokens?: number;
  emitSummaryChunk?: boolean;
  footnoteStrategy?: 'inline' | 'separate';
};

export type TaxRagEstimateStats = {
  items:  { total: number; updates: number; byDocType: Record<string, number>; byChannel: Record<string, number> };
  chunks: { total: number; body: number; summary: number; footnote: number };
  tokens: { body: number; headers: number; summary: number; footnote: number; total: number };
  attachments: { pdf: number; image: number };
  itemsWithNoBody: number;
};

export type TaxRagCost = {
  small: { standard: number; batch: number };
  large: { standard: number; batch: number };
};

export type TaxRagEstimate = {
  sourceUrl: string;
  sourceName: string;
  fileHash: string;
  zipHash: string;
  zipBytes: number;
  xmlBytes: number;
  stats: TaxRagEstimateStats;
  cost: TaxRagCost;
  wallClockEstimate: { seconds: number; concurrency: number; batchSize: number };
  chunkConfig: Required<TaxRagChunkConfig>;
  computedAt: string;
  cached: boolean;
  elapsedMs: number;
};

export type RunMode = 'full' | 'dry-run';

export type TaxRagRun = {
  run_id: string;
  source_name: string;
  source_url: string;
  source_file_hash: string | null;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'partial';
  mode: RunMode;
  started_at: string;
  finished_at: string | null;
  items_total: number;
  items_created: number;
  items_updated: number;
  items_skipped: number;
  items_failed: number;
  chunks_upserted: number;
  chunks_deleted: number;
  tokens_embedded: number;
  estimated_cost_usd: number;
  embedding_model: string | null;
  search_index_name: string | null;
  triggered_by: string | null;
  duration_seconds: number;
};

export type TaxRagRunSummary = {
  runId: string;
  status: 'succeeded' | 'failed' | 'partial';
  mode: RunMode;
  counts: {
    itemsTotal: number;
    itemsCreated: number;
    itemsUpdated: number;
    itemsSkipped: number;
    itemsFailed: number;
    chunksUpserted: number;
    chunksDeleted: number;
    tokensEmbedded: number;
    estimatedCostUsd: number;
  };
  errorSummary: string | null;
};

export type TaxRagInventoryItem = {
  guid: string;
  current_version: number;
  current_run_id: string;
  document_type: string | null;
  headline: string | null;
  rid: string | null;
  channel: string | null;
  chunk_count: number;
  content_modified_at: string | null;
  ingested_at: string;
  source_file: string | null;
};

export type TaxRagFailedItem = {
  failed_item_id: number;
  run_id: string;
  guid: string | null;
  version: number | null;
  stage: string;
  error_message: string;
  error_stack: string | null;
  raw_snippet: string | null;
  occurred_at: string;
  retry_count: number;
  resolved_at: string | null;
};

export type TaxRagIndexStatus =
  | { configured: false; hint: string }
  | {
      configured: true;
      endpoint?: string;
      indexName: string;
      exists?: boolean;
      documentCount?: number;
      storageSize?: number;
      vectorDimensions?: number | null;
    };

// ─────────────────────────────────────────────────────────────────────────────

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', cache: 'no-store', ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Server error ${res.status}`);
  }
  return res.json();
}

function post<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ── Sources + Estimator ──────────────────────────────────────────────────────

export type TaxRagSourcesResponse = {
  sources: TaxRagSource[];
  counts: { total: number; processed: number; unprocessed: number };
};

export function listTaxRagSources(): Promise<TaxRagSourcesResponse> {
  return api('/api/tax-rag-sources');
}

export function estimateTaxRagSource(
  sourceUrl: string,
  opts?: { chunkConfig?: TaxRagChunkConfig; bustCache?: boolean }
): Promise<TaxRagEstimate> {
  return post('/api/tax-rag-estimate', {
    sourceUrl,
    chunkConfig: opts?.chunkConfig,
    bustCache: opts?.bustCache ?? false,
  });
}

// ── Run + Runs ───────────────────────────────────────────────────────────────

export function runTaxRagSource(params: {
  sourceUrl: string;
  sourceName?: string;
  mode?: RunMode;
  embeddingModel?: string;
  chunkConfig?: TaxRagChunkConfig;
}): Promise<TaxRagRunSummary> {
  return post('/api/tax-rag-run', params);
}

export function listTaxRagRuns(limit = 50): Promise<{ runs: TaxRagRun[] }> {
  return api(`/api/tax-rag-runs?limit=${encodeURIComponent(String(limit))}`);
}

// ── Inventory ────────────────────────────────────────────────────────────────

export function listTaxRagInventory(params?: {
  limit?: number;
  offset?: number;
  documentType?: string;
  channel?: string;
  q?: string;
}): Promise<{ total: number; items: TaxRagInventoryItem[] }> {
  const qs = new URLSearchParams();
  if (params?.limit != null)       qs.set('limit', String(params.limit));
  if (params?.offset != null)      qs.set('offset', String(params.offset));
  if (params?.documentType)        qs.set('documentType', params.documentType);
  if (params?.channel)             qs.set('channel', params.channel);
  if (params?.q)                   qs.set('q', params.q);
  return api(`/api/tax-rag-inventory${qs.toString() ? `?${qs.toString()}` : ''}`);
}

// ── Corpus stats ─────────────────────────────────────────────────────────────

export type TaxRagCorpusStats = {
  total_documents: number;
  total_chunks: number;
  date_range: {
    content_earliest: string | null;
    content_latest: string | null;
    ingested_earliest: string | null;
    ingested_latest: string | null;
  };
  by_document_type: Record<string, number>;
  by_channel: Record<string, number>;
  by_tier: Array<{
    tier: number;
    label: string;
    count: number;
    document_types: Record<string, number>;
  }>;
  unknown_document_types: string[];
};

export function getTaxRagCorpusStats(): Promise<TaxRagCorpusStats> {
  return api('/api/tax-rag-corpus-stats');
}

// ── Retrieval Inspector ──────────────────────────────────────────────────────

export type TaxRagInspectChunk = {
  id: string;
  guid: string;
  rank: number;
  raw_score: number;
  boosted_score: number;
  authority_tier: number;
  authority_label: string;
  document_type: string | null;
  headline: string | null;
  citation: string | null;
  chunk_ordinal: number;
  chunk_type: string | null;
  content_modified_at: string | null;
  publication_current_date: string | null;
  content_excerpt: string;
  content_length: number;
  also_in_pass_a?: boolean;
  also_in_pass_b?: boolean;
  sources?: Array<'A' | 'B'>;
  source?: 'quota' | 'backfill';
  quotaTier?: number;
};

export type TaxRagInspectResult = {
  query: string;
  embeddingModel: string;
  passA: TaxRagInspectChunk[];
  passB: TaxRagInspectChunk[];
  passC: TaxRagInspectChunk[];
  candidatePool: TaxRagInspectChunk[];
  finalSelection: TaxRagInspectChunk[];
  tierSlots: Record<string, { id: string; document_type: string | null; raw_score: number } | null>;
  passCounts: {
    passA: number;
    passB: number;
    passBOnly: number;
    passC: number;
    passCOnly: number;
  };
  topScore: number;
  avgScore: number;
  strongCount: number;
  confidence: 'none' | 'low' | 'medium' | 'high';
  embedTokens: number;
};

export function inspectTaxRagRetrieval(query: string): Promise<TaxRagInspectResult> {
  return post('/api/tax-rag-inspect', { query });
}

// ── Failed items ─────────────────────────────────────────────────────────────

export function listTaxRagFailedItems(params?: {
  runId?: string;
  limit?: number;
  unresolved?: boolean;
}): Promise<{ items: TaxRagFailedItem[] }> {
  const qs = new URLSearchParams();
  if (params?.runId)            qs.set('runId', params.runId);
  if (params?.limit != null)    qs.set('limit', String(params.limit));
  if (params?.unresolved === false) qs.set('unresolved', 'false');
  return api(`/api/tax-rag-failed-items${qs.toString() ? `?${qs.toString()}` : ''}`);
}

// ── Index admin ──────────────────────────────────────────────────────────────

export function getTaxRagIndexStatus(): Promise<TaxRagIndexStatus> {
  return api('/api/tax-rag-index-status');
}

export function provisionTaxRagIndex(embeddingModel?: string): Promise<{
  created: boolean; updated: boolean; indexName: string; vectorDimensions: number; embeddingModel: string;
}> {
  return post('/api/tax-rag-index-provision', embeddingModel ? { embeddingModel } : {});
}
